/**
 * English UI catalogue of onboarding (namespace 'onboarding'; W2-14 onboarding-wizard, DESIGN.md §4.2, §5.14): the
 * first-run question, the tour, the sample project and the commands. zh-CN lives in src/i18n/zh-CN/onboarding.ts
 * (W3-09).
 *
 * Keys are never written into a text: a parameter carries the key the active keymap binds to the command now
 * (keys.tsx). Its value is 'none' when the command has no key, so every such text `select`s on it; in the tour the
 * bound value is drawn as key caps. Parameters: tourPlayBody {play, listen}, tourTapBody {tool, place},
 * tourHoldBody {tool}, tourCurveBody {curve}, tourPublishBody {publish}, tourFinished {help}.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('onboarding', {
  // ---- first-run question ----
  frTitle: 'Welcome to Phiedit',
  frIntro: 'Phiedit makes charts for Phira. How would you like to start?',
  frChoicesLabel: 'Your experience',
  frNew: "I'm new to charting",
  frNewBody: 'Guided level · plain hints · 2-minute tour',
  frRpe: "I've used RPE (Re:PhiEdit)",
  frRpeBody: 'Full level · RPE keyboard · "For RPE users" guide',
  frOther: "I've used other editors (osu!, Phichain, ...)",
  frOtherBody: 'Full level · Default keyboard',
  frLanguage: 'Language',
  langEn: 'English',
  langZh: '简体中文',
  frLater: 'You can change all of this later in Settings.',
  frContinue: 'Continue',

  // ---- tour ----
  tourStep: 'Step {n} of {total}',
  tourNext: 'Next',
  tourBack: 'Back',
  tourFirstStep: 'This is the first step.',
  tourFinish: 'Finish',
  tourSkip: 'Skip the tour',
  tourClose: 'Close the tour',
  tourDoneStep: 'Nice, that worked.',
  tourAnnounce: '{label}: {title}',
  tourNotOnScreen: 'This part is not on screen right now; the tour goes on when it appears.',
  tourNeedsChartTitle: 'The tour runs in a chart',
  tourNeedsChartBody: 'Open the sample chart and take the tour there?',
  tourOpenSample: 'Open the sample',
  tourFinished:
    'Tour finished. {help, select, none {You can take it again from the Help menu.} other {You can take it again from Help ({help}).}}',
  tourLineTitle: 'This is your line',
  tourLineBody:
    'Notes fall onto the judge line. The preview shows exactly what Phira will play; it is in Edit mode, so you can click and drag lines.',
  tourPlayTitle: 'Play the song',
  tourPlayBody:
    '{play, select, none {Click this button to play and pause.} other {Press {play} or click this button to play and pause.}}{listen, select, none {} other { Hold {listen} to listen only while the key is down.}}',
  tourTapTitle: 'Place a Tap',
  tourTapBody:
    '{tool, select, none {Pick the Tap tool} other {Pick the Tap tool ({tool})}} and click in the lane{place, select, none {.} other {, or press {place} with the mouse over the lane.}}',
  tourHoldTitle: 'Place a Hold',
  tourHoldBody:
    '{tool, select, none {Pick the Hold tool,} other {Pick the Hold tool ({tool}),}} press where the hold starts and drag up to where it ends.',
  tourDragTitle: 'Make the line move',
  tourCurveTitle: 'Change how it moves',
  tourCurveBody:
    'Click a segment between two keyframes in the motion columns and {curve, select, none {right-click it} other {press {curve}}} to pick a curve, such as "Slow end".',
  tourProblemsTitle: 'Keep an eye on Problems',
  tourProblemsBody: 'This chip counts problems. Click it to see each one in plain words, most with a Fix button.',
  tourProblemsBodyMore:
    'In a window this size, the problem count sits in the More menu. Open it and click the count to see each problem in plain words, most with a Fix button.',
  tourPublishTitle: 'Publish',
  tourPublishBody:
    'When you are ready, open Publish{publish, select, none {} other { ({publish})}}: it checks the chart and exports a .pez file that Phira plays exactly like the preview.',

  // ---- sample project ----
  sampleTitle: 'Phiedit sample',
  sampleIntro: 'A short chart on a synthesised drum loop that shows every line type, layers and a UI mover.',
  sampleCredit: 'Phiedit',
  sampleLineMain: 'Main',
  sampleLinePartner: 'Partner',
  sampleLineTitle: 'Title',
  sampleLinePicture: 'Picture',
  sampleLineSpinner: 'Spinner',
  sampleLineBrush: 'Brush',
  sampleLineScore: 'Score mover',
  sampleWordsFirst: 'Learn with a sample',
  sampleWordsSecond: 'Have fun charting',
  samplePreparing: 'Preparing the sample chart...',
  sampleFailed: 'The sample chart could not be created: {detail}',
  sampleExistsTitle: 'Open the sample chart',
  sampleExistsBody: 'You already have the sample chart in your Library.',
  sampleOpenExisting: 'Open my sample',
  sampleOpenExistingBody: 'Continue where you left off.',
  sampleFresh: 'Start a fresh copy',
  sampleFreshBody: 'A new, untouched sample next to yours.',

  // ---- commands ----
  cmdSampleOpen: 'Learn with a sample',
  cmdTourStart: 'Take the tour',
  cmdTourStop: 'End the tour',
  cmdHelpOpen: 'Help',
  cmdHelpRpe: 'Help: For RPE users',
  cmdHelpGlossary: 'Help: Words used in Phiedit',
  cmdHelpWelcome: 'Show the welcome question again',
  cmdHelpShowMe: 'Help: Show me',
  argTopic: 'Help topic',
  tourNotRunning: 'No tour is running.',
  showMeUnknown: 'There is no help topic "{topic}".',
  welcomeApplied: 'Your choices were applied.',
});

export default catalogOf(m);
